'use client'

import amplifyConfig from '@/amplify_outputs.json'
import { getBugsnag } from '@/utils/bugsnag.client'
import { getBugsnagAppVersion } from '@/utils/bugsnagConfig'
import {
  type CognitoDiagnostic,
  createCognitoDiagnostics,
  getCognitoDiagnosticErrorName,
} from './cognitoDiagnostics'

const poolEnvironment = () => {
  switch (amplifyConfig.auth?.user_pool_id) {
    case 'eu-central-1_7L8WP5jl5':
      return 'production' as const
    case 'eu-central-1_Ik2mmsZEf':
      return 'staging' as const
    default:
      return 'unknown' as const
  }
}

const routeTemplate = () => {
  if (typeof window === 'undefined') return '/other'
  const segments = window.location.pathname.split('/').filter(Boolean)
  if (['fr', 'en', 'de', 'nl'].includes(segments[0])) segments.shift()
  const root = segments[0]
  return [
    'login',
    'register',
    'signup',
    'onboarding',
    'organization',
    'account',
    'dashboard',
    'projects',
    'inventory',
    'references',
    'shop',
    'settings',
    'team',
    'orders',
    'alerts',
  ].includes(root)
    ? `/${root}/*`
    : '/other'
}

let pageInstanceId: string | undefined
export const reportCognitoDiagnostic = (diagnostic: CognitoDiagnostic) => {
  getBugsnag()?.notify(
    new Error(`Cognito ${diagnostic.operation} ${diagnostic.reason}`),
    (event) => {
      event.severity = 'warning'
      event.context = diagnostic.route_template
      event.groupingHash = `cognito-${diagnostic.operation}-${diagnostic.reason}`
      // These run after the SDK's device, request and user enrichment callbacks.
      event.setUser('[REDACTED]')
      event.request = { clientIp: '[REDACTED]' }
      event.breadcrumbs = []
      delete event.device.id
      delete event.device.hostname
      delete event.device.ip
      event.addMetadata('cognito_diagnostic', diagnostic)
    },
  )
}

export const cognitoMonitoring = createCognitoDiagnostics(
  reportCognitoDiagnostic,
  () => {
    const browser = typeof window !== 'undefined'
    const hostname = browser ? window.location.hostname : ''
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(hostname)
    if (browser)
      pageInstanceId ??=
        typeof globalThis.crypto?.randomUUID === 'function'
          ? globalThis.crypto.randomUUID()
          : `page-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    return {
      app_id: 'manage',
      build_version: getBugsnagAppVersion() ?? 'unknown',
      runtime: browser ? 'browser' : 'server',
      runtime_origin_class: local
        ? 'local'
        : hostname === 'manage.skop.app'
          ? 'production'
          : ['manage-preview.skop.app', 'manage-staging.skop.app'].includes(
                hostname,
              )
            ? 'staging'
            : 'other',
      cognito_environment: poolEnvironment(),
      route_template: routeTemplate(),
      ...(browser ? { page_instance_id: pageInstanceId } : {}),
    }
  },
  Date.now,
  (error) => {
    // Diagnostic filtering is independent of the consumer's logout policy.
    const name = getCognitoDiagnosticErrorName(error)
    return (
      !!name &&
      [
        'UserUnAuthenticatedException',
        'NotAuthorizedException',
        'TokenRevokedException',
        'UserNotFoundException',
        'PasswordResetRequiredException',
        'UserNotConfirmedException',
        'RefreshTokenReuseException',
      ].some((expected) => name.startsWith(expected))
    )
  },
)
